/**
 * Each remote voice in a moderator's room held at the volume the moderator
 * chose for it — muted, or heard — on this browser alone: the private call
 * mutes its player, the voice monitor any participant, or all of them.
 *
 * LiveKit has no mute for what one listener hears, so a muted voice is a
 * track turned down to nothing, and a track that comes back (the player
 * switches their microphone off and on) starts at full volume again. Both
 * windows wrote the same loop for it: set each audio track's volume now, every
 * 500ms after, and whenever a microphone's track changes. It is here once,
 * with whom the monitor's moderator hears (hearingAfter).
 */

import { useEffect } from 'react';
import { useRoomContext, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';

/** What setting a participant's volume reads of them: who they are, and their tracks. */
interface Heard {
  identity: string;
  audioTrackPublications: { values(): Iterable<{ kind: string; audioTrack?: unknown }> };
}

/** Whether a track can be turned down on this browser alone: a remote audio track can. */
const hasVolume = (track: unknown): track is { setVolume: (volume: number) => void } =>
  typeof track === 'object' && track !== null && 'setVolume' in track && typeof track.setVolume === 'function';

/**
 * Sets each participant's audio tracks to what `mutedFor` says of them: 0 when
 * muted, 1 when heard. One it says nothing of (undefined) is left as it is.
 */
export function applyRemoteVolumes(participants: Iterable<Heard>, mutedFor: (identity: string) => boolean | undefined): void {
  for (const participant of participants) {
    const muted = mutedFor(participant.identity);
    if (muted === undefined) continue;
    for (const publication of participant.audioTrackPublications.values()) {
      if (publication.kind === 'audio' && hasVolume(publication.audioTrack)) publication.audioTrack.setVolume(muted ? 0 : 1);
    }
  }
}

/**
 * Whom the voice monitor's moderator hears: everyone, or no one (its mute
 * all), and those turned the other way one at a time. One who joins while no
 * one is heard is muted with the rest; one turned back to heard stays heard
 * as others come and go; muting or hearing everyone starts afresh.
 *
 * The monitor kept the muted as a list of names, and while no one was heard
 * added every participant to it whenever anyone joined or left — one turned
 * back to heard was muted again by the next to come.
 */
export interface Hearing {
  /** Mute all is on: no one is heard but those turned. */
  allMuted: boolean;
  /** Those turned the other way from everyone else, by identity. */
  turned: ReadonlySet<string>;
}

/** As the monitor opens: everyone heard. */
export const HEARING_EVERYONE: Hearing = { allMuted: false, turned: new Set() };

/** What the moderator does: turns one participant, or everyone at once. */
export type HearingEvent = { type: 'turnOne'; identity: string } | { type: 'turnAll' };

/** Whom the moderator hears after what they did. */
export function hearingAfter(hearing: Hearing, event: HearingEvent): Hearing {
  switch (event.type) {
    case 'turnAll':
      return { allMuted: !hearing.allMuted, turned: new Set() };
    case 'turnOne': {
      const turned = new Set(hearing.turned);
      if (!turned.delete(event.identity)) turned.add(event.identity);
      return { allMuted: hearing.allMuted, turned };
    }
  }
}

/** Whether the moderator has muted a participant. */
export function mutedIn(hearing: Hearing, identity: string): boolean {
  return hearing.allMuted !== hearing.turned.has(identity);
}

/**
 * Holds the room's remote voices at what `mutedFor` says, from inside its
 * LiveKitRoom. `mutedFor` keeps its identity while what it says stays the
 * same (useCallback): a new one starts the loop again.
 */
export function useRemoteVolumes(mutedFor: (identity: string) => boolean | undefined): void {
  const room = useRoomContext();
  // A microphone's track coming and going: the volumes are set again at once.
  const audioTracks = useTracks([Track.Source.Microphone], { onlySubscribed: false });

  useEffect(() => {
    if (!room) return;
    const apply = () => applyRemoteVolumes(room.remoteParticipants.values(), mutedFor);
    apply();
    const interval = setInterval(apply, 500);
    return () => clearInterval(interval);
  }, [room, mutedFor, audioTracks]);
}
